import createModule from "../createModule"
import view from "./view"
import {
  getSortedContributors,
  getAvailableRollups,
  getContributorsSummaryText,
  getExcelData,
  getSelectedGroup,
  getSelectedGroupValue,
  getSelectedGroupSiblings,
  getSortedGroupContributors,
  getIncludeNotHeld,
} from "./selectors"
import { getGroupsDropdownColumns } from "./selectors/group"
import { getSelectedAccount } from "modules/account-root/selectors"
import { convertPositionSetType } from "helpers/compositeSecurities"
import { getPage } from "helpers/compositeSecuritiesDetails"
import { generateClassificationQueryForContributors } from "helpers/classification"
import { getQueryGroupBy } from "helpers/rollups"
import { Creators } from "./model"
import { Creators as ProfileCreators } from "model/profileModal"
import { Creators as CompositeCreators } from "modules/analyze-composite-security/model"
import { VIEW_TYPES } from "helpers/base"
import { getFactorSensitivityQueryParams } from "helpers/factorSensitivity"

export default createModule({
  view,
  externalProps: (state, props) => ({
    account: getSelectedAccount(state),
    includeNotHeld: getIncludeNotHeld(state, props),
  }),
  required: ["account", "positionSetType", "positionSetId", "modelId", "factorSensitivityType"],
  data: [{
    resource: "compositeFactorSensitivityContributors",
    as: null,
    url: ({ positionSetType, positionSetId, modelId, selectedDate, viewType, expand, base, baseSignature, segment, classifications, includeNotHeld, factorSensitivityType }) => {
      if (selectedDate) {
        return {
          id: `compositefactorSensitivityContributors-${factorSensitivityType}${modelId}${positionSetType}${positionSetId}${selectedDate}${viewType}${expand}${baseSignature}${JSON.stringify(segment)}${includeNotHeld}`,
          query: `query ($modelId: String!, $positionSet: PositionSetInput!, $date: Date!, $base: [PositionSetInput], $includeNotHeld: Boolean, $factorSensitivityType: FactorSensitivityType!, $stdDev: PositiveFloat, $days: PositiveInt, $horizon: FactorSensitivityTimeHorizon!) {
            model (id: $modelId) {
              simulation (positionSet: $positionSet, from: $date, to: $date, base: $base, interval: END_DATE) {
                factorSensitivityContributors (type: $factorSensitivityType, stdDev: $stdDev, days: $days, horizon: $horizon, includeNotHeld: $includeNotHeld) {
                  id
                  assetClass
                  assetSubclass
                  notHeld
                  country
                  ${generateClassificationQueryForContributors("GICS", classifications)}
                  currency
                  description
                  percentEquity
                  bloombergTicker
                  modelProviderId
                  factors {
                    id
                    category
                    sensitivity
                  }
                }
              }
            }
          }`,
          variables: {
            modelId,
            positionSet: {
              type: convertPositionSetType(positionSetType),
              id: positionSetId,
              segment,
              expand,
            },
            date: selectedDate,
            base: viewType === VIEW_TYPES.ACTIVE ? [base] : undefined,
            segment,
            includeNotHeld,
            expand,
            factorSensitivityType,
            ...getFactorSensitivityQueryParams(factorSensitivityType),
          },
        }
      }

      return null
    },
  }, {
    resource: "compositeGroupedFactorSensitivityContributors",
    as: null,
    url: ({ modelId, positionSetType, positionSetId, selectedDate, viewType, expand, base, baseSignature, currentGrouping, segment, classifications, includeNotHeld, factorSensitivityType }) => {
      if (!currentGrouping) {
        return null
      }

      const queryGroupBy = getQueryGroupBy(currentGrouping, viewType === VIEW_TYPES.ACTIVE)

      return {
        id: `compositeGroupedFactorSensitivityContributors-${factorSensitivityType}${modelId}${positionSetType}${positionSetId}${selectedDate}${viewType}${expand}${baseSignature}${JSON.stringify(queryGroupBy)}${JSON.stringify(segment)}${includeNotHeld}`,
        query: `query ($modelId: String!, $positionSet: PositionSetInput!, $date: Date!, $base: [PositionSetInput], $includeNotHeld: Boolean, $factorSensitivityType: FactorSensitivityType!, $stdDev: PositiveFloat, $days: PositiveInt, $groupBy: ContributorGroupType!, $groupById: String, $tier: String, $horizon: FactorSensitivityTimeHorizon!) {
          model (id: $modelId) {
            simulation (positionSet: $positionSet, from: $date, to: $date, base: $base, interval: END_DATE) {
              groupedFactorSensitivityContributors (type: $factorSensitivityType, stdDev: $stdDev, days: $days, horizon: $horizon, includeNotHeld: $includeNotHeld, groupBy: $groupBy, groupById: $groupById, classificationTier: $tier) {
                name
                id
                totalPercentEquity
                factors {
                  id
                  category
                  sensitivity
                }
                contributors {
                  id
                  assetClass
                  assetSubclass
                  notHeld
                  country
                  ${generateClassificationQueryForContributors("GICS", classifications)}
                  currency
                  description
                  percentEquity
                  bloombergTicker
                  modelProviderId
                  factors {
                    id
                    category
                    sensitivity
                  }
                }
              }
            }
          }
        }`,
        variables: {
          modelId,
          positionSet: {
            type: convertPositionSetType(positionSetType),
            id: positionSetId,
            segment,
            expand,
          },
          date: selectedDate,
          base: viewType === VIEW_TYPES.ACTIVE ? [base] : undefined,
          segment,
          includeNotHeld,
          expand,
          ...queryGroupBy,
          ...getFactorSensitivityQueryParams(factorSensitivityType),
        },
      }
    },
  }],
  mapStateToProps: (state, props) => ({
    contributors: getSortedContributors(state, props),
    groupedData: getSortedGroupContributors(state, props),
    availableRollups: getAvailableRollups(state, props),
    contributorsSummaryText: getContributorsSummaryText(state, props),
    excelData: getExcelData(state, props),
    selectedGroup: getSelectedGroup(state, props),
    selectedGroupValue: getSelectedGroupValue(state, props),
    selectedGroupSiblings: getSelectedGroupSiblings(state, props),
    groupsDropdownColumns: getGroupsDropdownColumns(state, props),
    page: getPage(state),
  }),
  actions: {
    ...Creators,
    openSecurityProfile: ProfileCreators.openSecurityProfile,
    openFactorProfile: ProfileCreators.openFactorProfile,
    openCompositeSecurity: CompositeCreators.openCompositeSecurity,
  },
})
